import { ReactNode, useEffect, useRef, useState } from "react";

import {
    FeatureName,
    useAuth,
    useCurrentUser,
    useCurrentWorkspace,
    useFeatureFlag,
} from "@accurx/auth";
import { Ds } from "@accurx/design";
import {
    StyledDropdownMenuContent,
    StyledDropdownMenuItem,
} from "@accurx/navigation";
import {
    isMyWorkspace,
    usePendingApproval,
} from "@accurx/workspace-management";
import { cleanUpSession } from "appLifecycle";
import { userflowIds } from "layouts/constants";
import { Link, generatePath, useHistory } from "react-router-dom";
import { useAnalytics } from "reduxQuarantine/useAnalytics";

import { ROUTES_WORKSPACE } from "shared/Routes";

import { getNavigationVersion } from "../../helpers";
import { HelpMenuContent } from "../HelpMenu/HelpMenu";
import { UnapprovedUsersBadge } from "../UnapprovedUsersBadge/UnapprovedUsersBadge";
import { WorkspaceSwitcherContent } from "../WorkspaceSwitcher/WorkspaceSwitcher";
import { DropdownSubmenu } from "./DropdwonSubmenu";
import {
    StyledTooltipTriggerWrapper,
    StyledWorkspaceMenuButton,
} from "./WorkspaceMenu.styles";
import { WorkspaceMenuHeader } from "./WorkspaceMenuHeader";

const WorkspaceMenuTooltip = ({ children }: { children: ReactNode }) => {
    const workspace = useCurrentWorkspace();
    const { organisationName: workspaceName, organisationNodeName } = workspace;
    const { user } = useCurrentUser();
    const isFlexibleWorkspaceEnabled = useFeatureFlag(
        FeatureName.FlexibleWorkspace,
    );

    return (
        <Ds.Tooltip>
            <StyledTooltipTriggerWrapper>
                <Ds.Tooltip.Trigger tabIndex={-1}>
                    {children}
                </Ds.Tooltip.Trigger>
            </StyledTooltipTriggerWrapper>
            <Ds.Tooltip.Content side="bottom" align="center">
                <Ds.Flex flexDirection="column" alignItems="center">
                    <Ds.Text weight="bold" color="white">
                        {isMyWorkspace(workspace)
                            ? "My Workspace"
                            : workspaceName}
                    </Ds.Text>
                    {isFlexibleWorkspaceEnabled && (
                        <Ds.Text weight="bold" color="white">
                            {organisationNodeName}
                        </Ds.Text>
                    )}
                    <Ds.Text weight="bold" color="white">
                        {user.fullName}
                    </Ds.Text>
                </Ds.Flex>
            </Ds.Tooltip.Content>
        </Ds.Tooltip>
    );
};

type Menu = "Default" | "Help" | "WorkspaceSwitcher";

type MenuItem =
    | "Settings"
    | "Users"
    | "Reporting"
    | "Logout"
    | "Help"
    | "WorkspaceSwitcher";

export const WorkspaceMenu = ({
    children,
    isMobile = false,
}: {
    children?: ReactNode;
    isMobile?: boolean;
}) => {
    const workspace = useCurrentWorkspace();
    const { orgId: workspaceId, organisationName: workspaceName } = workspace;
    const { user } = useCurrentUser();
    const { logOut } = useAuth();
    const { isPendingApproval } = usePendingApproval(workspaceId);

    const contentRef = useRef<HTMLDivElement | null>(null);

    const track = useAnalytics();
    const history = useHistory();

    // Mobile-specific
    const [currentMenu, setCurrentMenu] = useState<Menu>("Default");

    useEffect(() => {
        isMobile && setTimeout(() => contentRef.current?.focus(), 0);
    }, [isMobile, currentMenu]);

    const trackMenuItemClick = (optionSelected: MenuItem) => {
        track("NavigationOrganisation MenuItem Click", {
            eventVersion: 2,
            navigationVersion: getNavigationVersion(),
            origin: history.location.pathname,
            optionSelected,
        });
    };

    const handleOpenChange = (open: boolean) => {
        if (open) {
            setCurrentMenu("Default");
            track("NavigationMenu Button Click", {
                origin: history.location.pathname,
                navigationOptionSelected: "WorkspaceMenu",
                navigationVersion: getNavigationVersion(),
                eventVersion: 2,
            });
        }
    };

    const handleMenuItemClick = (item: MenuItem) => {
        trackMenuItemClick(item);
    };

    const handleLogOut = () => {
        trackMenuItemClick("Logout");
        logOut({ cleanUpFn: cleanUpSession });
    };

    const handleHelpMenuClick = (e: Event) => {
        e.preventDefault(); // Keep menu open
        setCurrentMenu("Help");
        trackMenuItemClick("Help");
    };

    const handleWorkspaceSwitcherMenuClick = (e: Event) => {
        e.preventDefault(); // Keep menu open
        setCurrentMenu("WorkspaceSwitcher");
        trackMenuItemClick("WorkspaceSwitcher");
    };

    const handleBackClick = (e: Event) => {
        e.preventDefault();
        setCurrentMenu("Default");
    };

    return (
        <Ds.DropdownMenu onOpenChange={handleOpenChange} modal={false}>
            {isMobile ? (
                <Ds.DropdownMenu.Trigger asChild>
                    {children || (
                        <StyledWorkspaceMenuButton
                            data-userflow-id={
                                userflowIds.navigation.primary.workspaceMenu
                            }
                            aria-label={`${workspaceName} workspace menu`}
                        >
                            <Ds.Icon name="Cog" size="small" color="white" />
                        </StyledWorkspaceMenuButton>
                    )}
                </Ds.DropdownMenu.Trigger>
            ) : (
                <WorkspaceMenuTooltip>
                    <Ds.DropdownMenu.Trigger asChild>
                        {children || (
                            <StyledWorkspaceMenuButton
                                data-userflow-id={
                                    userflowIds.navigation.primary.workspaceMenu
                                }
                                aria-label={`${workspaceName} workspace menu`}
                            >
                                <Ds.Icon
                                    name="Cog"
                                    size="small"
                                    color="white"
                                />
                                {workspaceName}
                                {isPendingApproval && (
                                    <Ds.Icon
                                        name="LockLocked"
                                        appearance="solid"
                                        size="xsmall"
                                        color="white"
                                    />
                                )}
                            </StyledWorkspaceMenuButton>
                        )}
                    </Ds.DropdownMenu.Trigger>
                </WorkspaceMenuTooltip>
            )}
            {currentMenu === "Help" ? (
                <HelpMenuContent
                    ref={contentRef}
                    workspaceId={workspaceId}
                    onBack={handleBackClick}
                />
            ) : currentMenu === "WorkspaceSwitcher" ? (
                <WorkspaceSwitcherContent
                    ref={contentRef}
                    onBack={handleBackClick}
                />
            ) : (
                <StyledDropdownMenuContent
                    ref={contentRef}
                    side="bottom"
                    align="end"
                    sideOffset={4}
                >
                    <Ds.DropdownMenu.Header>
                        <WorkspaceMenuHeader
                            workspace={workspace}
                            user={user}
                        />
                    </Ds.DropdownMenu.Header>

                    {isPendingApproval && (
                        <Ds.Badge color="greyscale">
                            <Ds.Icon
                                name="LockLocked"
                                appearance="solid"
                                size="xsmall"
                                color="night"
                            />
                            Pending approval
                        </Ds.Badge>
                    )}
                    <Ds.DropdownMenu.Item
                        asChild
                        onSelect={() => handleMenuItemClick("Settings")}
                    >
                        <StyledDropdownMenuItem
                            as={Link}
                            to={generatePath(
                                "/w/:workspaceId/settings/templates",
                                { workspaceId },
                            )}
                            data-userflow-id={
                                userflowIds.navigation.primary
                                    .workspaceMenuSettings
                            }
                        >
                            <Ds.Icon name="Cog" size="small" />
                            <Ds.Text>Settings</Ds.Text>
                        </StyledDropdownMenuItem>
                    </Ds.DropdownMenu.Item>
                    {workspace.settings.isApprovedUser && (
                        <Ds.DropdownMenu.Item
                            asChild
                            onSelect={() => handleMenuItemClick("Users")}
                        >
                            <StyledDropdownMenuItem
                                as={Link}
                                to={generatePath(
                                    "/w/:workspaceId/settings/users",
                                    { workspaceId },
                                )}
                                data-userflow-id={
                                    userflowIds.navigation.primary
                                        .workspaceMenuUsers
                                }
                            >
                                <Ds.Flex gap="1">
                                    <Ds.Icon name="Team" size="small" />
                                    <Ds.Text>Users</Ds.Text>
                                </Ds.Flex>
                                <UnapprovedUsersBadge />
                            </StyledDropdownMenuItem>
                        </Ds.DropdownMenu.Item>
                    )}

                    {workspace.settings.isApprovedUser && (
                        <Ds.DropdownMenu.Item
                            asChild
                            onSelect={() => handleMenuItemClick("Reporting")}
                        >
                            <StyledDropdownMenuItem
                                as={Link}
                                to={generatePath(ROUTES_WORKSPACE.reporting, {
                                    workspaceId,
                                })}
                                data-userflow-id={
                                    userflowIds.navigation.primary
                                        .workspaceMenuReporting
                                }
                            >
                                <Ds.Icon name="Chart" size="small" />
                                <Ds.Text>Reporting</Ds.Text>
                            </StyledDropdownMenuItem>
                        </Ds.DropdownMenu.Item>
                    )}

                    <Ds.DropdownMenu.Divider />

                    {isMobile && (
                        <>
                            <DropdownSubmenu
                                onSelect={handleHelpMenuClick}
                                data-userflow-id={
                                    userflowIds.navigation.primary
                                        .workspaceMenuHelp
                                }
                            >
                                <Ds.Icon
                                    name="Question"
                                    appearance="outline"
                                    size="small"
                                />
                                <Ds.Text>Help</Ds.Text>
                            </DropdownSubmenu>

                            <Ds.DropdownMenu.Divider />

                            <DropdownSubmenu
                                onSelect={handleWorkspaceSwitcherMenuClick}
                                data-userflow-id={
                                    userflowIds.navigation.primary
                                        .workspaceMenuWorkspaceSwitcher
                                }
                                data-testid="primary-nav-workspace-switcher-button"
                            >
                                <Ds.Icon name="preview-Swap" size="small" />
                                <Ds.Text>Switch workspace</Ds.Text>
                            </DropdownSubmenu>
                        </>
                    )}

                    <StyledDropdownMenuItem
                        onSelect={handleLogOut}
                        data-userflow-id={
                            userflowIds.navigation.primary.workspaceMenuLogout
                        }
                    >
                        <Ds.Icon name="preview-Logout" size="small" />
                        <Ds.Text>Log out</Ds.Text>
                    </StyledDropdownMenuItem>
                </StyledDropdownMenuContent>
            )}
        </Ds.DropdownMenu>
    );
};
